// LivestreamDetailOnlineHeader.jsx
import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import UniversalComponent from '../component/UniversalComponent.jsx';
import Tooltip from '../component/Tooltip.jsx';

import {
  liveStreamSessionLeaderboardCategory,
  RANGE_SESSION,
} from '../resource/liveStreamConstants.js';
import { ButtonId } from '../resource/mixpanel.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import { color } from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';

import HotLiveIcon from '../../img/ic-hot-live.svg';

export const LivestreamDetailOnlineHeader = ({
  streamId = null,
  viewers = '-',
  needStreamTokenForExclusivePrivatePerforming = false,
  hotLivestreamHintOpenedTimestamp = 0,
  toggleModal = NULL_FUNCTION,
  setHotLivestreamHintOpenedTimestamp = NULL_FUNCTION,
  t = NULL_FUNCTION,
}) => {
  const trackingPayload = useMemo(
    () => ({
      'stream.id': streamId,
    }),
    [streamId]
  );

  // Tooltip auto-display logic
  const shouldShowHotLivestreamHint = !hotLivestreamHintOpenedTimestamp;

  const handleTooltipClosed = useCallback(() => {
    if (shouldShowHotLivestreamHint) {
      setHotLivestreamHintOpenedTimestamp({
        hotLivestreamHintOpenedTimestamp: Date.now(),
      });
    }
  }, [shouldShowHotLivestreamHint, setHotLivestreamHintOpenedTimestamp]);
  return (
    <StyledLivestreamDetailOnlineHeader>
      <LiveStreamUserWrapper>
        <UniversalComponent
          filename="Container_LiveStreamUser"
          userId={streamId}
          streamId={streamId}
          importer={
            /* #__LOADABLE__ */ () => import('../container/LiveStreamUser.js')
          }
        />
      </LiveStreamUserWrapper>

      {needStreamTokenForExclusivePrivatePerforming ? null : (
        <Tooltip
          key={shouldShowHotLivestreamHint ? 'show-tooltip' : 'hide-tooltip'}
          position="bottom-center"
          trigger="auto"
          onHintClosed={handleTooltipClosed}
          renderHint={() =>
            shouldShowHotLivestreamHint ? (
              <HotLivestreamTooltipWrapper>
                {t('tips_livestream_update_trending_and_followed_creators', {
                  ns: TranslationNamespace.LIVESTREAM,
                })}
              </HotLivestreamTooltipWrapper>
            ) : null
          }
        >
          <HotLivestreamButton
            data-element_id={ButtonId.LiveStream.ButtonExploreMoreLivestream}
            data-tracking_payload={trackingPayload}
            onClick={() => {
              toggleModal({
                modalId: 'LivestreamExploreMoreModal',
                isOpen: true,
              });
            }}
          >
            <img src={HotLiveIcon} alt="hot-live" />
          </HotLivestreamButton>
        </Tooltip>
      )}

      <StatusPanel>
        <StatusLeft>
          <UniversalComponent
            filename="Container_LivestreamDetailStreamLabel"
            streamId={streamId}
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/LivestreamDetailStreamLabel.js')
            }
          />
        </StatusLeft>
        {!needStreamTokenForExclusivePrivatePerforming && (
          <StatusRight>
            <ToyCommandRow>
              <UniversalComponent
                filename="Container_ToyCommandAnimationPanel"
                streamId={streamId}
                isToy
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/ToyCommandAnimationPanel.js')
                }
              />
            </ToyCommandRow>
            <ToyCommandRow>
              <UniversalComponent
                filename="Container_ToyCommandAnimationPanel"
                streamId={streamId}
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/ToyCommandAnimationPanel.js')
                }
              />
            </ToyCommandRow>
          </StatusRight>
        )}
      </StatusPanel>
      {needStreamTokenForExclusivePrivatePerforming ? null : (
        <LeaderboardPanel>
          <UniversalComponent
            filename="Container_LiveStreamTopLeaderboard"
            streamId={streamId}
            range={RANGE_SESSION}
            category={liveStreamSessionLeaderboardCategory.TOTAL}
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/LiveStreamTopLeaderboard.js')
            }
          />
          <ViewersText>{viewers}</ViewersText>
        </LeaderboardPanel>
      )}
    </StyledLivestreamDetailOnlineHeader>
  );
};

LivestreamDetailOnlineHeader.propTypes = {
  streamId: PropTypes.string,
  viewers: PropTypes.string,
  needStreamTokenForExclusivePrivatePerforming: PropTypes.bool,
  hotLivestreamHintOpenedTimestamp: PropTypes.number,
  toggleModal: PropTypes.func,
  setHotLivestreamHintOpenedTimestamp: PropTypes.func,
  t: PropTypes.func.isRequired,
};

const StyledLivestreamDetailOnlineHeader = styled.header`
  position: relative;
  min-height: 64px;
  width: 100%;
  padding: 12px 8px 8px;
  user-select: none;
  z-index: 2;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  grid-template-areas:
    'user-panel leaderboard'
    'status status';

  ${media.tablet`
    grid-template-columns: auto 44px 1fr;
    grid-template-areas:
      'user-panel hot-live leaderboard'
      'status status status';
  `};
`;

const LiveStreamUserWrapper = styled.div`
  grid-area: user-panel;
  z-index: 1;
  display: flex;
  align-items: center;
`;

const HotLivestreamButton = styled.button`
  ${ResetButtonStyle};
  margin-left: 4px;
  border-radius: 50%;
  padding: 6px;
  grid-area: hot-live;
  display: none;
  width: 40px;
  height: 40px;
  background-color: ${color.neutral['0_alpha_20']};
  > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  ${media.tablet`
    display: flex;
    align-items: center;
    justify-content: center;
  `};
`;

const LeaderboardPanel = styled.div`
  grid-area: leaderboard;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 28px;
  margin-left: 16px;
  margin-right: 40px;
  overflow: hidden;
  ${media.tablet`
    margin-right: 32px;
  `};
  > [class^='UniversalAnchor_'] {
    width: auto !important;
    overflow: hidden;
  }
`;

const StatusPanel = styled.div`
  grid-area: status;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 12px;
  z-index: 1;
`;

const StatusLeft = styled.div`
  display: inline-flex;
  align-items: flex-start;
`;

const StatusRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ToyCommandRow = styled.div`
  display: inline-flex;
`;

const ViewersText = styled.div`
  margin-left: 2px;
  margin-right: 4px;
  border-radius: 18px;
  padding: 0px 4px;
  flex: none;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  background-color: ${color.neutral['0_alpha_20']};
  color: ${color.neutral[100]};
`;

const HotLivestreamTooltipWrapper = styled.div`
  position: relative;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  margin-top: 8px;
  color: ${color.neutral[100]};
  background-color: ${color.neutral[0]};
  text-align: center;
  display: none;
  width: max-content;
  max-width: 250px;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  box-shadow: 0px 2px 8px 0px rgba(25, 25, 25, 0.2);

  ${media.tablet`
    display: inline-block;
  `};

  &::after {
    position: absolute;
    top: -4px;
    left: 50%;
    width: 8px;
    height: 8px;
    background-color: ${color.neutral[0]};
    transform: translateX(-50%) rotate(45deg);
    content: '';
  }
`;

export default withTranslation()(LivestreamDetailOnlineHeader);
